import type { HTMLAttributes, KeyboardEvent as ReactKeyboardEvent, ReactNode, Ref } from 'react';
import { IcnClose } from '../../icons/index.js';
import { useI18nT } from '../../i18n/index.js';

export type ChipTone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger';
export type ChipSize = 'sm' | 'md';

export interface ChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'className'> {
  icon?: ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
  tone?: ChipTone;
  size?: ChipSize;
  disabled?: boolean;
  /** When true, the chip itself is focusable (tabIndex=0) and supports
   *  Backspace/Delete to invoke `onRemove`. Defaults to true when `onRemove`
   *  is set — gives keyboard users parity with the × button click. */
  focusable?: boolean;
  className?: string;
  ref?: Ref<HTMLSpanElement>;
  children: ReactNode;
}

/** Dismissible chip. Optional leading icon + trailing close button.
 *  Unlike Tag (non-interactive), Chip is typically removable via onRemove.
 *  Keyboard: when focused, Backspace/Delete triggers onRemove (Gmail-style). */
export function Chip(props: ChipProps): ReactNode {
  const t = useI18nT();
  const {
    icon,
    onRemove,
    removeLabel = t('chip.removeLabel'),
    tone = 'neutral',
    size = 'md',
    disabled = false,
    focusable,
    className,
    ref,
    children,
    onKeyDown,
    ...rest
  } = props;

  const isFocusable = focusable ?? onRemove !== undefined;
  const classes = ['vq-chip', className].filter(Boolean).join(' ');

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLSpanElement>): void => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (disabled) return;
    if (onRemove === undefined) return;
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      onRemove();
    }
  };

  return (
    <span
      {...rest}
      ref={ref}
      className={classes}
      data-component="Chip"
      data-tone={tone}
      data-size={size}
      data-state={disabled ? 'disabled' : 'idle'}
      tabIndex={isFocusable && !disabled ? 0 : undefined}
      onKeyDown={handleKeyDown}
    >
      {icon !== undefined ? (
        <span className="vq-chip__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="vq-chip__label">{children}</span>
      {onRemove !== undefined ? (
        <button
          type="button"
          className="vq-chip__remove"
          aria-label={removeLabel}
          disabled={disabled}
          tabIndex={-1 /* chip itself owns focus when focusable; avoid double-stop */}
          onClick={onRemove}
        >
          <IcnClose size={12} />
        </button>
      ) : null}
    </span>
  );
}
